import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@cp/ds/src/components/box';
import { BoxProps } from '@cp/ds/src/components/box/model';
import { ClientProfileViewModel } from '@common/model/client';
import { TutorIcon } from '@cp/ds/src/icons/career/colored/tutor';
import { StepType, TrainingAnswersBlock } from '@common/model/client/training/types';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { getCareerIcon } from '@cp/entities/career';
import { VideoStep } from 'src/features/client/training/ui/steps/videoStep';
import { QuestionStep } from 'src/features/client/training/ui/steps/questionStep';
import { FlowResult } from 'src/features/client/training/ui/steps/flowResult';
import { BlockResult } from '../steps/blockResult';
import { StepWelcome } from '../steps/welcome';
import { StepProps } from '../steps/model';
import { FlowStepBlockResult, FlowStepBlockStep, FlowStepType, useTrainingService } from '../../model';

export interface TrainingFlowProps extends BoxProps {
  clientProfile: ClientProfileViewModel;
  onComplete: () => void;
}

export const TrainingFlow: React.FC<TrainingFlowProps> = ({ clientProfile, onComplete, ...props }) => {
  const service = useTrainingService();
  const Icon = useMemo(() => (clientProfile.mainCareerId ? getCareerIcon(clientProfile.mainCareerId) : TutorIcon), [clientProfile]);
  const [step, setStep] = useState(service.getStep());
  const [stepState, setStepState] = useState<'success' | 'failure' | undefined>(undefined);
  const [values, setValues] = useState<TrainingAnswersBlock | undefined>();

  const submitStep: StepProps['onSubmit'] = useCallback(
    async (v) => {
      await service.completeStep(v);
      setStep(service.getStep());
      setStepState(undefined);
      setValues(undefined);
    },
    [service],
  );
  const validateStep: StepProps['onSubmit'] = useCallback(
    (v) => {
      if (!v) {
        return submitStep(v);
      }
      const valid = service.validateStep(v);
      setStepState(valid ? 'success' : 'failure');
      setValues(v);
      return Promise.resolve();
    },
    [service, submitStep],
  );

  useEffect(() => {
    if (!clientProfile.mainCareerId) {
      console.warn('Training without careerId');
      return;
    }
    service.startTraining(clientProfile.mainCareerId).then(() => {
      const s = service.getStep();
      setStep(s);
    });
  }, [clientProfile, service]);

  let StepComponent: (p: StepProps) => JSX.Element | null = () => null;
  if (step) {
    switch (step.value.type) {
      case FlowStepType.BlockStep:
        StepComponent = (p: StepProps) => {
          const model = step.value as FlowStepBlockStep;
          return model.step.stepType === StepType.Video ? (
            <VideoStep meta={model.meta} model={model.step} showAlert={model.meta.id === 1} {...p} />
          ) : (
            <QuestionStep meta={model.meta} model={model.step} {...p} />
          );
        };
        break;
      case FlowStepType.BlockResult:
        StepComponent = (p: StepProps) => <BlockResult errorsCount={(step.value as FlowStepBlockResult).errorsCount} icon={Icon} {...p} />;
        break;
      case FlowStepType.Welcome:
        StepComponent = StepWelcome;
        break;
      case FlowStepType.Finish:
        StepComponent = (p: StepProps) => (
          <FlowResult
            icon={Icon}
            {...p}
            onSubmit={async () => {
              await submitStep();
              onComplete();
            }}
          />
        );
        break;
      default:
        StepComponent = () => null;
    }
  }

  return (
    <Box {...props}>
      {step ? (
        <StepComponent onSubmit={stepState ? submitStep : validateStep} state={stepState} values={values} />
      ) : (
        <ContentSkeleton blocks={3} />
      )}
    </Box>
  );
};
